import { useState, useEffect } from 'react'
import { ImagesFilters } from '@/components/Images/ImagesFilters'
import { ImagesGrid } from '@/components/Images/ImagesGrid'
import { BulkUploadImagesModal } from '@/components/Images/BulkUploadImagesModal'
import { Pagination } from '@/components/common/Pagination'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import {
  useImagesList,
  useUpdateImage,
  useDeleteImage,
} from '@/hooks/useImages'
import { toast } from '@/stores/useToastStore'

export default function AdminImages() {
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [searchInput, pageSize])

  const { data, isLoading, isError } = useImagesList({
    search: searchInput,
    page,
    pageSize,
  })

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load images')
    }
  }, [isError])

  const updateImage = useUpdateImage()
  const deleteImageMutation = useDeleteImage()

  const images = data?.data ?? []
  const total = data?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const handleUpdate = (
    imageId: string,
    updateData: { name?: string; description?: string }
  ) => {
    updateImage.mutate({ imageId, data: updateData })
  }

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return
    deleteImageMutation.mutate(deleteTarget, {
      onSuccess: () => setDeleteTarget(null),
    })
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Images</h1>
        <p className="text-base text-muted-foreground mt-1">
          Upload and manage images for line items
        </p>
      </div>

      {/* Filter Bar */}
      <ImagesFilters
        searchInput={searchInput}
        onSearchChange={setSearchInput}
        onUploadClick={() => setShowUploadModal(true)}
      />

      {/* Grid */}
      <ImagesGrid
        images={images}
        loading={isLoading}
        onUpdate={handleUpdate}
        onDelete={id => setDeleteTarget(id)}
        updatingId={
          updateImage.isPending ? updateImage.variables?.imageId : undefined
        }
      />

      {/* Pagination */}
      <Pagination
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      {/* Upload Modal */}
      <BulkUploadImagesModal
        open={showUploadModal}
        onOpenChange={setShowUploadModal}
      />

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={!!deleteTarget}
        onOpenChange={open => {
          if (!open) setDeleteTarget(null)
        }}
        title="Delete Image"
        description="Are you sure you want to delete this image? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        isLoading={deleteImageMutation.isPending}
      />
    </div>
  )
}
